import React from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  CheckCircle,
  DeleteOutline,
  Edit,
  OpenInNew,
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  time: {
    minWidth: 52,
    fontWeight: 700,
    fontSize: "0.875rem",
    color: theme.palette.primary.main,
    paddingTop: 2,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  empty: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 1),
  },
}));

const getStatusChip = (task) => {
  if (task.status === "completed") {
    return { label: i18n.t("tasks.status.completed"), color: "#059669" };
  }
  const date = parseISO(task.remindAt);
  const now = new Date();
  if (date < now) {
    return { label: i18n.t("tasks.status.overdue"), color: "#DC2626" };
  }
  return { label: i18n.t("tasks.tabs.pending"), color: "#2563EB" };
};

const TaskDayDetailModal = ({
  open,
  onClose,
  date,
  tasks = [],
  highlightTaskId = null,
  onEdit,
  onComplete,
  onDelete,
  onGoToTicket,
  onCreate,
}) => {
  const classes = useStyles();

  const sorted = [...tasks].sort(
    (a, b) => new Date(a.remindAt).getTime() - new Date(b.remindAt).getTime()
  );

  const titleDate = date
    ? format(date, "EEEE d 'de' MMMM yyyy", { locale: es })
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {i18n.t("tasks.calendar.dayDetail")}
        <Typography variant="body2" color="textSecondary" style={{ textTransform: "capitalize" }}>
          {titleDate}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {sorted.length === 0 ? (
          <Box className={classes.empty}>
            <Typography variant="body1">{i18n.t("tasks.calendar.noTasksDay")}</Typography>
          </Box>
        ) : (
          sorted.map((task) => {
            const chip = getStatusChip(task);
            const isHighlight = highlightTaskId && Number(task.id) === Number(highlightTaskId);

            return (
              <Box
                key={task.id}
                className={classes.row}
                style={
                  isHighlight
                    ? { background: "rgba(37, 99, 235, 0.06)", borderRadius: 8, padding: 12 }
                    : undefined
                }
              >
                <Typography className={classes.time}>
                  {format(parseISO(task.remindAt), "HH:mm")}
                </Typography>
                <Box className={classes.content}>
                  <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {task.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={chip.label}
                      style={{
                        backgroundColor: `${chip.color}18`,
                        color: chip.color,
                        fontWeight: 600,
                        height: 22,
                      }}
                    />
                  </Box>
                  {task.body && (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                      {task.body}
                    </Typography>
                  )}
                  {task.contact?.name && (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                      {i18n.t("tasks.contact")}: {task.contact.name}
                    </Typography>
                  )}
                </Box>
                <Box className={classes.actions}>
                  {task.status === "pending" && (
                    <IconButton size="small" onClick={() => onComplete(task)} title={i18n.t("tasks.markDone")}>
                      <CheckCircle style={{ color: "#059669", fontSize: 20 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => onEdit(task)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  {task.ticket?.uuid && (
                    <IconButton size="small" onClick={() => onGoToTicket(task)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => onDelete(task)}>
                    <DeleteOutline fontSize="small" style={{ color: "#DC2626" }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("tasks.calendar.close")}</Button>
        {onCreate && (
          <Button color="primary" variant="contained" onClick={() => onCreate(date)}>
            {i18n.t("tasks.new")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TaskDayDetailModal;
